import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import { Box, Button, Paper, TextField, Typography } from '@material-ui/core';

import agent from 'api/agent';
import { GitHubTokenStatus } from 'api/models';
import globalStore from 'store/globalStore';
import { formatDateTimeGermany } from 'helpers/datetimeHelper';
import { useStyles } from '../styles';
import { FormSkeleton } from 'components/shared';
import { useFirstLoad } from 'hooks/useFirstLoad';

export default function GitHubTokenForm() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [status, setStatus] = useState<GitHubTokenStatus | null>(null);
    const [token, setToken] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isStatusLoading, settleStatus] = useFirstLoad();

    function getStatus() {
        agent.AppUpdates.GetTokenStatus()
            .then(setStatus)
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(settleStatus);
    }

    useEffect(() => {
        getStatus();
    }, []);

    function closeForm() {
        setToken('');
        setIsFormOpen(false);
    }

    function saveToken() {
        setIsSaving(true);
        agent.AppUpdates.SaveToken(token.trim())
            .then(() => {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('appDistribution.tokenSaved')
                    )
                );
                setIsFormOpen(false);
                getStatus();
            })
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => {
                setToken('');
                setIsSaving(false);
            });
    }

    function renderStatus() {
        if (!status) return null;

        if (!status.hasToken) {
            return (
                <Typography variant="body2" className={classes.warning}>
                    {i18n.t('appDistribution.tokenMissing')}
                </Typography>
            );
        }

        return (
            <Typography variant="body2">
                {i18n.t('appDistribution.tokenPresent', {
                    last4: status.last4,
                    updatedAt: status.updatedAt
                        ? formatDateTimeGermany(new Date(status.updatedAt))
                        : '-',
                })}
            </Typography>
        );
    }

    return (
        <Paper className={classes.tokenPanel}>
            <h6 className={classes.sectionTitle}>{i18n.t('appDistribution.githubToken')}</h6>
            <Box mb={2}>{renderStatus()}</Box>
            {isFormOpen ? (
                <div className={classes.tokenRow}>
                    <TextField
                        className={classes.tokenInput}
                        variant="filled"
                        type="password"
                        autoComplete="new-password"
                        label={i18n.t('appDistribution.newToken')}
                        helperText={i18n.t('appDistribution.tokenHelp')}
                        value={token}
                        onChange={(event) => setToken(event.target.value)}
                    />
                    <Button color="primary" disabled={isSaving} onClick={closeForm}>
                        {i18n.t('button.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSaving || !token.trim()}
                        onClick={saveToken}
                    >
                        {i18n.t('common.save')}
                    </Button>
                </div>
            ) : (
                <Button variant="contained" color="primary" onClick={() => setIsFormOpen(true)}>
                    {i18n.t('appDistribution.addNewToken')}
                </Button>
            )}
            <FormSkeleton isLoading={isStatusLoading} columns={[1]} actions={1} />
        </Paper>
    );
}
